import { CommonModule, DOCUMENT } from "@angular/common";
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Inject,
  Input,
  Output,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import {
  Alert,
  BottomNavigationPanel,
  FormConfiguration,
  FormFactory,
  FunnelHeader,
  FunnelHeaderMilestone,
  InfoDialog,
  Section,
} from "pax-ui/components";
import { Emoji } from "projects/pax-ui/components/emoji/emoji-alt-mapping";
import { CheckoutFormWrapperComponent } from "src/app/quote/shared/components/checkout-form-wrapper/checkout-form-wrapper.component";
import { ExternalSystemCommunicationStateService } from "src/app/shared/services/external-system-communication-state.service";

import { OPPORTUNITY_CENTER_TEL_FORMATTED } from "src/app/shared/models/contact";
import { PremiumFormModel } from "../base/base.page";

@Component({
  selector: "app-checkout-page",
  imports: [
    FormFactory,
    FunnelHeader,
    Section,
    BottomNavigationPanel,
    CheckoutFormWrapperComponent,
    CommonModule,
    Alert,
  ],
  templateUrl: "./checkout-page.html",
  styleUrls: ["./checkout-page.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CheckoutPage<T = PremiumFormModel> {
  @ViewChild("infoDialogTemplate")
  formInfoDialog!: TemplateRef<HTMLElement>;

  readonly telephoneLink = `<a href="tel:${OPPORTUNITY_CENTER_TEL_FORMATTED.trim()}">${OPPORTUNITY_CENTER_TEL_FORMATTED}</a>`;

  @Input({ required: true })
  allMilestones: FunnelHeaderMilestone[] | undefined;

  @Input({ required: true })
  formConfiguration!: FormConfiguration;

  @Input({ required: true })
  data!: T;

  @Input({ required: true })
  sectionTitle: string | undefined;

  @Input({ required: true })
  subtitle: string | undefined;

  @Input({ required: true })
  emojis: Emoji[] | undefined;

  @Input({ required: true })
  consultationAppointmentUrl: URL | undefined;

  @Input({ required: true })
  infoDialog: InfoDialogWithLegalDisclaimer | undefined;

  @Input()
  enableSkipButton = false;

  @Input()
  enableBackButton = true;

  @Output()
  navigateBack = new EventEmitter<void>();

  @Output()
  navigateNext = new EventEmitter<void>();

  @Output()
  formSubmit = new EventEmitter<T>();

  @Output()
  formChange = new EventEmitter<T>();

  constructor(
    @Inject(DOCUMENT) private readonly _document: Document,
    public readonly externalSystemCommunicationStateService: ExternalSystemCommunicationStateService,
  ) {}

  get infoDialogEnhanced(): InfoDialogWithLegalDisclaimer | undefined {
    return (
      this.infoDialog && {
        ...this.infoDialog,
        contentTemplate: this.formInfoDialog,
      }
    );
  }

  get legalDisclaimerInfoMarkdown(): string {
    return $localize`**Wichtige Information:** Die obigen Angaben sind allgemeine Richtwerte und können eine persönliche Beratung im Einzelfall nicht ersetzen.\n\nFür eine persönliche Beratung <a target="_blank" href="${this.consultationAppointmentUrl?.toString()}">vereinbaren Sie einen Termin</a> oder melden Sie sich telefonisch unter ${this.telephoneLink}:telephoneLink:.`;
  }

  submitForm() {
    const form = this._document.forms.namedItem(this.formConfiguration.name);
    if (form) {
      form.requestSubmit();
    } else {
      throw new Error(
        `Could not find a form with the name '${this.formConfiguration.name}'`,
      );
    }
  }
}

export interface InfoDialogWithLegalDisclaimer extends InfoDialog {
  showLegalDisclaimer?: boolean;
}
